/*
 * argile-blog.js — Page blog éditorial : pastilles de catégorie, panneau « Affiner »
 * (recherche + cases de catégories), tri, et « Voir la suite ».
 *
 * Pastilles et cases pilotent UN SEUL état (`cats`) : la pastille pose une catégorie unique,
 * les cases en posent plusieurs, et `syncControls()` reflète l'état sur les deux surfaces.
 * Deux jeux de filtres indépendants pour la même donnée seraient incompréhensibles.
 *
 * Shopify n'expose ni facettes ni tri sur un blog : tout est fait côté client, sur le
 * modèle de argile-stores.js. Les articles sont rendus dans le DOM, le JS bascule la
 * visibilité (classe `is-hidden`) et réordonne les cartes au tri.
 *
 * ── HYDRATATION ────────────────────────────────────────────────────────────────────
 * Une boucle Liquid ne rend que 50 articles et `paginate` plafonne à 50 par page : au-delà,
 * filtrer sur ce qui est rendu ne porterait que sur les 50 premiers, en silence. La première
 * interaction récupère donc les pages 2..N AVANT d'appliquer quoi que ce soit, via l'API de
 * rendu de section (`?section_id=…&page=N`, motif de assets/product-recommendations.js) —
 * qui ne rend que cette section au lieu de la page entière — et en parallèle.
 *
 * Sur un blog de moins de 50 articles il n'y a qu'une page : `hydrate()` sort immédiatement
 * et aucune requête n'est jamais émise.
 */
(() => {
  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const NEWSLETTER_AFTER = 20;

  const init = (root) => {
    const grid = root.querySelector('[data-blog-grid]');
    if (!grid) return;

    const pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 24;
    const blogUrl = root.getAttribute('data-blog-url') || '';
    const sectionId = root.getAttribute('data-blog-section') || '';
    const blogTotal = parseInt(root.getAttribute('data-blog-total'), 10) || 0;
    const pagesEl = root.querySelector('[data-blog-pages]');
    const totalPages = pagesEl
      ? parseInt(pagesEl.getAttribute('data-blog-pages'), 10) || 1
      : 1;

    const pills = Array.from(root.querySelectorAll('[data-blog-cat]'));
    const searchInput = root.querySelector('[data-blog-search]');
    const facetInputs = () => Array.from(root.querySelectorAll('[data-blog-facet]'));
    const facetCounts = () => Array.from(root.querySelectorAll('[data-blog-facet-count]'));
    const drawer = root.querySelector('[data-blog-drawer]');
    const openBtn = root.querySelector('[data-blog-open-filters]');
    const closeBtns = Array.from(root.querySelectorAll('[data-blog-close-filters]'));
    const clearBtn = root.querySelector('[data-blog-clear]');
    const bubble = root.querySelector('[data-blog-filter-count]');
    const sortWrap = root.querySelector('[data-blog-sort]');
    const sortBtns = sortWrap
      ? Array.from(sortWrap.querySelectorAll('[data-sort]'))
      : [];
    const emptyEl = root.querySelector('[data-blog-empty]');
    const newsletter = root.querySelector('[data-blog-newsletter]');
    const loadMore = root.querySelector('[data-blog-loadmore]');
    const moreBtn = root.querySelector('[data-blog-more]');
    const shownEl = root.querySelector('[data-blog-shown]');
    const totalEl = root.querySelector('[data-blog-total-count]');

    // Un SEUL état de catégories, partagé par les pastilles (sélection unique) et par les
    // cases du panneau (sélection multiple). Les deux surfaces se reflètent l'une l'autre.
    let cats = new Set();
    let query = '';
    let sort = 'recent';
    let visible = pageSize;
    let hydrated = totalPages <= 1;
    let hydrating = null;
    let meta = [];

    // ── Index des cartes. Reconstruit après hydratation.
    const buildMeta = () => {
      meta = Array.from(grid.querySelectorAll('.argile-blog__item')).map((li, i) => {
        const a = li.querySelector('[data-article-card]');
        return {
          li,
          order: i,
          categorie: a ? a.getAttribute('data-categorie') || '' : '',
          balises: a
            ? (a.getAttribute('data-balises') || '').split(' ').filter(Boolean)
            : [],
          date: a ? parseInt(a.getAttribute('data-date'), 10) || 0 : 0,
          titre: a ? a.getAttribute('data-titre') || '' : '',
          recherche: a ? norm(a.getAttribute('data-recherche')) : '',
        };
      });
    };

    // Un article répond à une rubrique s'il la porte, POINT — pas seulement si c'est celle
    // de son badge. `data-categorie` ne retient que la première ; `data-balises` porte la
    // liste complète du métachamp `custom.tag`, qui est un tableau. Ne comparer que
    // `data-categorie` rendrait muette la seconde rubrique d'un article qui en aurait deux.
    const porte = (m, value) => m.categorie === value || m.balises.includes(value);

    // Catégories ∩ recherche. `ignore === 'cats'` sert au recomptage par facette : le
    // compteur d'une catégorie doit ignorer la sélection de catégories, sinon en cocher
    // une ramènerait toutes les autres à zéro.
    const match = (m, ignore) => {
      if (ignore !== 'cats' && cats.size) {
        let touche = false;
        cats.forEach((c) => {
          if (porte(m, c)) touche = true;
        });
        if (!touche) return false;
      }
      if (query && !m.recherche.includes(query)) return false;
      return true;
    };

    // Reflète `cats` sur les deux surfaces. Une pastille n'est active que si elle est la
    // SEULE catégorie retenue ; « Tous » l'est quand la sélection est vide.
    const syncControls = () => {
      pills.forEach((p) => {
        const v = p.getAttribute('data-blog-cat') || '';
        const on = v === '' ? cats.size === 0 : cats.size === 1 && cats.has(v);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      facetInputs().forEach((i) => {
        i.checked = cats.has(i.value);
      });
    };

    const applySort = () => {
      const sorted = [...meta];
      if (sort === 'titre') sorted.sort((a, b) => a.titre.localeCompare(b.titre, 'fr'));
      else if (sort === 'titre-desc')
        sorted.sort((a, b) => b.titre.localeCompare(a.titre, 'fr'));
      else sorted.sort((a, b) => b.date - a.date || a.order - b.order);

      sorted.forEach((m) => grid.appendChild(m.li));

      // ⚠️ `meta` doit suivre le nouvel ordre, pas seulement le DOM. render() fait
      // `retenus.slice(0, visible)` : si `meta` restait dans l'ordre initial, « Voir la
      // suite » révélerait les N premiers de l'ANCIEN ordre, pas de celui affiché.
      meta = sorted;

      // Ces trois-là repassent en fin de grille ; render() les replace ensuite, une fois
      // la visibilité connue.
      if (newsletter) grid.appendChild(newsletter);
      if (loadMore) grid.appendChild(loadMore);
      if (emptyEl) grid.appendChild(emptyEl);
    };

    const updateFacetCounts = () => {
      facetCounts().forEach((el) => {
        const value = el.getAttribute('data-blog-facet-count');
        const n = meta.filter((m) => porte(m, value) && match(m, 'cats')).length;
        el.textContent = '(' + n + ')';
        const input = facetInputs().find((i) => i.value === value);
        if (input && !input.checked) input.disabled = n === 0;
      });

      if (bubble) {
        bubble.textContent = cats.size;
        bubble.hidden = cats.size === 0;
      }
    };

    // Ordonne les deux blocs pleine largeur de la grille.
    //
    // « Voir la suite » suit TOUJOURS la dernière carte affichée. L'encart newsletter
    // s'intercale après la 20e carte — mais seulement s'il reste des cartes derrière lui.
    // Sinon il passe APRÈS le bouton : autrement, sur un jeu plus court que le seuil,
    // le bouton se retrouvait isolé derrière un grand bandeau.
    const positionnerBlocs = (visibleList) => {
      if (visibleList.length === 0) {
        if (newsletter) newsletter.hidden = true;
        return;
      }
      const derniere = visibleList[visibleList.length - 1].li;

      if (newsletter && visibleList.length > NEWSLETTER_AFTER) {
        visibleList[NEWSLETTER_AFTER - 1].li.after(newsletter);
        newsletter.hidden = false;
        if (loadMore) derniere.after(loadMore);
      } else {
        if (loadMore) derniere.after(loadMore);
        if (newsletter) {
          newsletter.hidden = false;
          (loadMore || derniere).after(newsletter);
        }
      }
    };

    const render = () => {
      const retenus = meta.filter((m) => match(m, null));

      meta.forEach((m) => m.li.classList.add('is-hidden'));
      const affiches = retenus.slice(0, visible);
      affiches.forEach((m) => m.li.classList.remove('is-hidden'));

      if (emptyEl) emptyEl.hidden = retenus.length !== 0;
      positionnerBlocs(affiches);

      if (loadMore) {
        // Tant que l'hydratation n'a pas eu lieu et qu'aucun filtre n'est actif, le total
        // du blog est la seule valeur honnête : le jeu chargé n'en est qu'une partie.
        const filtreActif = Boolean(cats.size || query);
        const total = !hydrated && !filtreActif ? blogTotal : retenus.length;
        const reste = total > affiches.length;

        loadMore.hidden = total <= pageSize;
        if (moreBtn) moreBtn.style.display = reste ? '' : 'none';
        if (shownEl) shownEl.textContent = affiches.length;
        if (totalEl) totalEl.textContent = total;
      }

      updateFacetCounts();
    };

    // ── Récupération des pages 2..N. Idempotente et non réentrante : les appels
    // concurrents partagent la même promesse.
    const hydrate = () => {
      if (hydrated) return Promise.resolve();
      if (hydrating) return hydrating;

      root.setAttribute('aria-busy', 'true');

      const urls = [];
      for (let p = 2; p <= totalPages; p += 1) {
        const u = new URL(blogUrl, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        u.searchParams.set('page', String(p));
        urls.push(u.toString());
      }

      hydrating = Promise.all(
        urls.map((u) =>
          fetch(u)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        )
      )
        .then((pages) => {
          const fragment = document.createDocumentFragment();
          pages.forEach((html) => {
            if (!html) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            // Uniquement les cartes d'article : l'encart newsletter n'est rendu qu'en
            // page 1 côté Liquid, mais on l'exclut explicitement — une régression de ce
            // côté-là produirait des formulaires en double, silencieusement.
            doc
              .querySelectorAll('.argile-blog__item')
              .forEach((li) => fragment.appendChild(li));
          });
          grid.appendChild(fragment);
          hydrated = true;
          buildMeta();
          applySort();
        })
        .finally(() => {
          hydrating = null;
          root.removeAttribute('aria-busy');
        });

      return hydrating;
    };

    // Toute interaction de la barre d'outils passe par ici : elle hydrate d'abord, puis
    // applique. C'est ce qui garantit que filtres, recherche et tri portent sur TOUT le blog.
    const interagir = (fn) => {
      if (hydrated) {
        fn();
        render();
        return;
      }
      hydrate().then(() => {
        fn();
        render();
      });
    };

    // ── Pastilles de catégorie (sélection unique) + deep-link ?categorie=
    const setCategorie = (value) => {
      cats = value ? new Set([value]) : new Set();
      syncControls();
      visible = pageSize;
    };

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const value = pill.getAttribute('data-blog-cat') || '';
        interagir(() => setCategorie(value));

        const u = new URL(window.location.href);
        if (value) u.searchParams.set('categorie', value);
        else u.searchParams.delete('categorie');
        window.history.replaceState({}, '', u);
      });
    });

    // ── Recherche
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        const v = norm(e.target.value);
        interagir(() => {
          query = v;
          visible = pageSize;
        });
      });
    }

    // ── Panneau « Affiner ». L'ouverture hydrate : sinon les compteurs par thème
    // porteraient sur les seuls articles déjà chargés, et changeraient sous les yeux
    // de l'utilisateur au premier clic.
    const closeDrawer = () => {
      if (!drawer) return;
      if (drawer.open && typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');
    };

    if (drawer && openBtn) {
      openBtn.addEventListener('click', () => {
        if (typeof drawer.showModal === 'function') drawer.showModal();
        else drawer.setAttribute('open', '');
        interagir(() => {});
      });
      drawer.addEventListener('click', (e) => {
        if (e.target === drawer) closeDrawer();
      });
    }
    closeBtns.forEach((b) => b.addEventListener('click', closeDrawer));

    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-blog-facet]')) return;
      const choisies = facetInputs().filter((i) => i.checked).map((i) => i.value);
      interagir(() => {
        cats = new Set(choisies);
        syncControls();
        visible = pageSize;
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        facetInputs().forEach((i) => {
          i.disabled = false;
        });
        if (searchInput) searchInput.value = '';
        interagir(() => {
          cats = new Set();
          query = '';
          syncControls();
          visible = pageSize;
        });
      });
    }

    // ── Tri
    sortBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.getAttribute('data-sort');
        sortBtns.forEach((b) =>
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false')
        );
        interagir(() => {
          sort = value;
          applySort();
          visible = pageSize;
        });
        if (sortWrap) sortWrap.removeAttribute('open');
      });
    });

    document.addEventListener('click', (e) => {
      if (sortWrap && sortWrap.open && !sortWrap.contains(e.target)) {
        sortWrap.removeAttribute('open');
      }
    });

    // ── « Voir la suite ». C'est un vrai lien vers la page suivante (indexable, et
    // fonctionnel sans JS) : on ne neutralise le clic que tant qu'on peut servir la
    // demande côté client.
    if (moreBtn) {
      moreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const suite = visible + pageSize;
        if (!hydrated && suite > meta.length) {
          hydrate().then(() => {
            visible = suite;
            render();
          });
        } else {
          visible = suite;
          render();
        }
      });
    }

    // ── Démarrage. Un deep-link ?categorie= est une interaction comme une autre :
    // sans hydratation, le visiteur arrivant du menu verrait un filtre appliqué aux
    // seuls 50 premiers articles, sans que rien ne le signale.
    buildMeta();
    const depart = new URL(window.location.href).searchParams.get('categorie');
    const connue = depart && pills.some((p) => p.getAttribute('data-blog-cat') === depart);
    if (connue) {
      interagir(() => setCategorie(depart));
    } else {
      render();
    }
  };

  const boot = () => document.querySelectorAll('[data-blog]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
